import React, { type FC, type PropsWithChildren, useEffect, useRef, useState } from 'react';
import { type CssLength } from '../interfaces';

import './TruncatedText.scss';

interface TruncatedTextBasicProps {
    maxWidth?: CssLength | '';
    tooltipOverride?: string;
}

interface TruncatedTextWithChildrenProps extends TruncatedTextBasicProps {
    children: React.ReactNode;
    text?: never;
}

interface TruncatedTextWithTextProps extends TruncatedTextBasicProps {
    text: string;
    children?: never;
}

type TruncatedTextProps = TruncatedTextWithChildrenProps | TruncatedTextWithTextProps;

export const TruncatedText: FC<PropsWithChildren<TruncatedTextProps>> = ({ text, maxWidth = '', tooltipOverride = '', children }) => {
    const textRef = useRef<HTMLSpanElement>(null);
    const [tooltipText, setTooltipText] = useState<string | undefined>(tooltipOverride);

    const updateTooltip = () => {
        if (textRef.current) {
            const textContent = textRef.current.innerText;
            const isTextOverflowing = textRef.current.scrollWidth > textRef.current.clientWidth;

            setTooltipText(isTextOverflowing ? (tooltipOverride || textContent) : undefined);
        }
    };

    useEffect(() => {
        updateTooltip();
        window.addEventListener('resize', updateTooltip);
        return () => {
            window.removeEventListener('resize', updateTooltip);
        };
    }, [text, tooltipOverride, children]);

    return (
        <span 
            ref={textRef} 
            data-tooltip={tooltipText}
            className="truncated-text"
            style={maxWidth ? { maxWidth } : undefined}
        >
            {text || children}
        </span>
    );
};
